import React from "react";
import { BackgroundColorPanel, BackgroundPatternPanel } from "../bottom-background-menus";
import { BottomBackgroundMenu } from "../bottom-background-menus/bottom-background-menu";
import { DesignsPanel } from "../bottom-designs-menus";
import { LayoutsPanel } from "../bottom-layouts-menu";
import { AlternativeProductsBottomMenuPanel } from "../bottom-menu-panels/alternative-products";
import { ProductOptionsBottomMenuPanel } from "../bottom-menu-panels/product-options";
import { StartDateSelectionBottomMenuPanel } from "../bottom-menu-panels/start-date-selection/start-date-selection-panel";
import { SymbolsRootMenuContainer } from "../bottom-menu-panels/symbols/symbol-root-menu/symbols-root-menu-container";
import { SymbolsPanelContainer as SymbolsListPanel } from "../bottom-menu-panels/symbols/symbols-list/symbols-list-panel-container";
import { BottomRootMenu } from "../bottom-menus/bottom-root-menu";
import { OptionsBottomMenu } from "../bottom-menus/options";
import { BottomMenuId, BottomMenuIds } from "./paths";

enum SliderType {
	Contrast = "contrast",
	Brightness = "brightness",
	Saturation = "saturation",
}

/** Created a function so import always happens with the webpackChunkName comment */
function getTextMenusAsync() {
	return import(
		/* webpackChunkName: "express-text-menus" */
		"../bottom-text-menus"
	).then((module) => module.default);
}

function getPhotoMenusAsync() {
	return import(
		/* webpackChunkName: "express-photo-menus" */
		"../bottom-photo-menus"
	).then((module) => module.default);
}

function getQrMenusAsync() {
	return import(
		/* webpackChunkName: "express-qr-menus" */
		"../bottom-qr-menus"
	).then((module) => module.default);
}

export const createComponentAsync = async (menuId: string | undefined) => {
	const bottomMenuId = menuId as BottomMenuId | undefined;
	switch (bottomMenuId) {
		case BottomMenuIds.Root: {
			return <BottomRootMenu />;
		}

		case BottomMenuIds.Text: {
			const { BottomTextMenu } = await getTextMenusAsync();
			return <BottomTextMenu />;
		}
		case BottomMenuIds.TextValue: {
			const { TextValuePanel } = await getTextMenusAsync();
			return <TextValuePanel />;
		}
		case BottomMenuIds.TextSize: {
			const { TextSizePanel } = await getTextMenusAsync();
			return <TextSizePanel />;
		}
		case BottomMenuIds.TextAlignment: {
			const { TextAlignmentMenu } = await getTextMenusAsync();
			return <TextAlignmentMenu />;
		}
		case BottomMenuIds.FontFamilies: {
			const { FontFamiliesPanel } = await getTextMenusAsync();
			return <FontFamiliesPanel />;
		}
		case BottomMenuIds.TextStyle: {
			const { TextStylePanel } = await getTextMenusAsync();
			return <TextStylePanel />;
		}
		case BottomMenuIds.TextColor: {
			const { TextColorPanel } = await getTextMenusAsync();
			return <TextColorPanel />;
		}

		case BottomMenuIds.Photo: {
			const { BottomPhotoMenu } = await getPhotoMenusAsync();
			return <BottomPhotoMenu />;
		}

		case BottomMenuIds.PhotoZoneDepth: {
			const { ZoneDepthPanel } = await getPhotoMenusAsync();
			return <ZoneDepthPanel />;
		}

		case BottomMenuIds.PhotoEditing: {
			const { BottomPhotoEditingMenu } = await getPhotoMenusAsync();
			return <BottomPhotoEditingMenu />;
		}

		case BottomMenuIds.PhotoTransform: {
			const { PhotoTransformPanel } = await getPhotoMenusAsync();
			return <PhotoTransformPanel />;
		}

		case BottomMenuIds.PhotoFilters: {
			const { PhotoFiltersPanel } = await getPhotoMenusAsync();
			return <PhotoFiltersPanel />;
		}

		case BottomMenuIds.PhotoBrightness: {
			const { PhotoEditingPanel } = await getPhotoMenusAsync();
			return <PhotoEditingPanel property={SliderType.Brightness} />;
		}

		case BottomMenuIds.PhotoSaturation: {
			const { PhotoEditingPanel } = await getPhotoMenusAsync();
			return <PhotoEditingPanel property={SliderType.Saturation} />;
		}

		case BottomMenuIds.PhotoContrast: {
			const { PhotoEditingPanel } = await getPhotoMenusAsync();
			return <PhotoEditingPanel property={SliderType.Contrast} />;
		}

		case BottomMenuIds.PhotoSelection: {
			const { PhotoSelectionPanel } = await getPhotoMenusAsync();
			return <PhotoSelectionPanel />;
		}

		case BottomMenuIds.Qr: {
			const { BottomQrMenu } = await getQrMenusAsync();
			return <BottomQrMenu />;
		}
		case BottomMenuIds.QrAdd: {
			const { QrValuePanel } = await getQrMenusAsync();
			return <QrValuePanel />;
		}

		case BottomMenuIds.QrEdit: {
			const { QrValuePanel } = await getQrMenusAsync();
			return <QrValuePanel isEdit />;
		}

		case BottomMenuIds.Backgrounds: {
			return <BottomBackgroundMenu />;
		}

		case BottomMenuIds.BackgroundColor: {
			return <BackgroundColorPanel />;
		}

		case BottomMenuIds.BackgroundPattern: {
			return <BackgroundPatternPanel />;
		}

		case BottomMenuIds.Symbols: {
			return <SymbolsRootMenuContainer />;
		}

		case BottomMenuIds.SymbolsList: {
			return <SymbolsListPanel />;
		}

		case BottomMenuIds.Layouts: {
			return <LayoutsPanel />;
		}

		case BottomMenuIds.Designs: {
			return <DesignsPanel />;
		}

		case BottomMenuIds.Options: {
			return <OptionsBottomMenu />;
		}

		case BottomMenuIds.AlternativeProducts: {
			return <AlternativeProductsBottomMenuPanel />;
		}

		case BottomMenuIds.ProductOptions: {
			return <ProductOptionsBottomMenuPanel />;
		}

		case BottomMenuIds.SymbolsZoneDepth:
		case BottomMenuIds.TextZoneDepth: {
			// TODO: Remove zoneDepth from PhotoMenu
			// Otherwise we also need to load the photo menu for text only products
			const { ZoneDepthPanel } = await getPhotoMenusAsync();
			return <ZoneDepthPanel />;
		}

		case BottomMenuIds.DateSelection: {
			return <StartDateSelectionBottomMenuPanel />;
		}

		// No sub menu's
		case BottomMenuIds.Events:
		case BottomMenuIds.Map:
		case BottomMenuIds.PhotoMap:
		case BottomMenuIds.PhotoRotate:
		case BottomMenuIds.PhotoZoneAdd:
		case BottomMenuIds.PhotoZoneClear:
		case BottomMenuIds.PhotoZoom:
		case BottomMenuIds.PhotoZoneDelete:
		case BottomMenuIds.QrDelete:
		case BottomMenuIds.SymbolsZoneDelete:
		case BottomMenuIds.TextZoneAdd:
		case BottomMenuIds.TextZoneClear:
		case BottomMenuIds.TextZoneDelete:
		case BottomMenuIds.None: {
			return undefined;
		}

		default: {
			eXpress.utils.exhaustiveFail(bottomMenuId, false);
			return undefined;
		}
	}
};
